// components/patients/CreatePatientModal.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import {
  PATIENT_GENDER_OPTIONS,
  buildPatientInitials,
  buildPatientNameFromParts,
  normalizePatientGender,
  validatePatientIdentityFields,
} from '../../../lib/patientIdentity';
import { isAdminAssistantRole } from '../../../lib/dentistSubmissionWizard';

const EMPTY_FORM = (userProfileId) => ({
  first_name: '',
  last_name: '',
  gender: '',
  email: '',
  phone: '',
  location: '',
  status: 'active',
  date_birth: '',
  user_profile_id: userProfileId,
});

const CreatePatientModal = ({
  isOpen,
  onClose,
  onPatientCreated,
  onCreateCaseForPatient = null,
  currentUserId,
  zIndexClassName = 'z-50',
}) => {
  const { userProfile: authUser } = useAuth();
  const [formData, setFormData] = useState(EMPTY_FORM(currentUserId));
  const [dentistClients, setDentistClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDentists, setLoadingDentists] = useState(false);
  const [error, setError] = useState(null);

  const canManageAllPatients = isAdminAssistantRole(authUser?.roleTypes);

  useEffect(() => {
    if (isOpen && canManageAllPatients) {
      fetchDentistClients();
    }
  }, [isOpen, canManageAllPatients]);

  useEffect(() => {
    if (isOpen && !canManageAllPatients) {
      setFormData((prev) => ({ ...prev, status: 'active' }));
    }
  }, [isOpen, canManageAllPatients]);

  useEffect(() => {
    if (isOpen) {
      setFormData(EMPTY_FORM(currentUserId));
      setError(null);
    }
  }, [isOpen, currentUserId]);

  const fetchDentistClients = async () => {
    try {
      setLoadingDentists(true);

      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('id')
        .eq('role_type', 'dentist_client')
        .single();

      if (roleError) throw roleError;

      const { data: assignments, error: assignmentsError } = await supabase
        .from('user_role_assignments')
        .select('user_id')
        .eq('role_id', roleData.id)
        .eq('is_active', true);

      if (assignmentsError) throw assignmentsError;

      const userIds = assignments.map((a) => a.user_id);

      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, company_name')
        .in('id', userIds)
        .eq('is_active', true)
        .order('full_name');

      if (profilesError) throw profilesError;

      setDentistClients(profiles || []);
    } catch (err) {
      console.error('Error fetching dentist clients:', err);
      setError('Impossible de charger la liste des dentistes');
    } finally {
      setLoadingDentists(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resolveSelectedDentistProfile = () => {
    if (!canManageAllPatients) return null;
    return dentistClients.find((d) => d.id === formData.user_profile_id) ?? null;
  };

  const createPatient = async () => {
    const fieldErrors = validatePatientIdentityFields(formData);
    if (Object.keys(fieldErrors).length > 0) {
      throw new Error(Object.values(fieldErrors)[0]);
    }

    if (canManageAllPatients && !formData.user_profile_id) {
      throw new Error('Veuillez sélectionner un dentiste');
    }

    const emailTrimmed = formData.email?.trim() || '';
    if (emailTrimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
      throw new Error("Format d'email invalide.");
    }

    const firstName = formData.first_name.trim();
    const lastName = formData.last_name.trim();
    const fullName = buildPatientNameFromParts(firstName, lastName);
    const initials = buildPatientInitials(firstName, lastName);

    const { data, error: insertError } = await supabase
      .from('patients')
      .insert({
        user_profile_id: formData.user_profile_id,
        first_name: firstName,
        last_name: lastName,
        gender: normalizePatientGender(formData.gender),
        name: fullName,
        initials,
        email: emailTrimmed || null,
        phone: formData.phone?.trim() || null,
        location: formData.location?.toLowerCase() || null,
        status: canManageAllPatients ? formData.status : 'active',
        date_birth: formData.date_birth,
        active_cases: 0,
        registration_date: new Date().toISOString(),
        last_visit: null,
      })
      .select()
      .single();

    if (insertError) throw insertError;
    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const data = await createPatient();
      setFormData(EMPTY_FORM(currentUserId));
      onPatientCreated(data);
      onClose();
    } catch (err) {
      console.error('Error creating patient:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCase = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await createPatient();
      const dentistProfile = resolveSelectedDentistProfile();
      setFormData(EMPTY_FORM(currentUserId));
      onPatientCreated(data);
      onCreateCaseForPatient?.(data, dentistProfile);
      onClose();
    } catch (err) {
      console.error('Error creating patient:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(EMPTY_FORM(currentUserId));
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 ${zIndexClassName} flex items-center justify-center`}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative bg-white dark:bg-card border border-border rounded-lg shadow-lg w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto text-card-foreground">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="UserPlus" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Nouveau Patient</h2>
              <p className="text-sm text-muted-foreground">Ajouter un nouveau patient à votre liste</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-destructive/10 border border-destructive rounded-lg p-3 flex items-start gap-2">
              <Icon name="AlertCircle" size={18} className="text-destructive mt-0.5" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {canManageAllPatients && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Dentiste / Client <span className="text-destructive">*</span>
              </label>
              {loadingDentists ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  Chargement des dentistes...
                </div>
              ) : (
                <select
                  name="user_profile_id"
                  value={formData.user_profile_id}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                >
                  <option value="">Sélectionner un dentiste</option>
                  {dentistClients.map((dentist) => (
                    <option key={dentist.id} value={dentist.id}>
                      {dentist.full_name || dentist.email}
                      {dentist.company_name && ` - ${dentist.company_name}`}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Prénom <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="Prénom"
                required
                autoComplete="given-name"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Nom <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Nom"
                required
                autoComplete="family-name"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Date de naissance <span className="text-destructive">*</span>
              </label>
              <input
                type="date"
                name="date_birth"
                value={formData.date_birth}
                onChange={handleChange}
                required
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Sexe <span className="text-destructive">*</span>
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              >
                <option value="">Sélectionner</option>
                {PATIENT_GENDER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="patient@example.com"
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Téléphone</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="00213 6X XX XX XX"
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Localisation</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Ville, Région"
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
            />
          </div>

          {canManageAllPatients && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Statut</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              >
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
              </select>
            </div>
          )}

          <div className={`flex flex-col gap-3 pt-4 ${onCreateCaseForPatient ? 'sm:flex-row sm:flex-wrap' : ''}`}>
            <Button type="button" variant="outline" onClick={handleClose} className="flex-1 min-w-[120px]" disabled={loading}>
              Annuler
            </Button>
            <Button
              type="submit"
              variant="default"
              className="flex-1 min-w-[120px]"
              disabled={loading || (canManageAllPatients && loadingDentists)}
              iconName={loading ? 'Loader2' : 'Check'}
              iconPosition="left"
            >
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
            {onCreateCaseForPatient ? (
              <Button
                type="button"
                variant="success"
                className="flex-1 min-w-[160px] sm:basis-full"
                disabled={loading || (canManageAllPatients && loadingDentists)}
                iconName={loading ? 'Loader2' : 'Send'}
                iconPosition="left"
                onClick={handleCreateCase}
              >
                {loading ? 'Enregistrement...' : 'Créer une commande'}
              </Button>
            ) : null}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePatientModal;
